/**
 * UISubsystem (W-UI, UI-8) — the player-side mirror of the editor's UI subsystem: mounts
 * `core.UICanvas` components from the build's UI bundle (manifest.ui → assets/ui.json) and
 * pumps input into the shared UIInputSystem.
 *
 * Parity contract: mount/scaler/update logic mirrors `packages/editor/src/rendering/
 * UISubsystem.ts`; input semantics live in @engine/ui (UIInputSystem + pollInput) so the two
 * hosts CANNOT drift there. Differences here: modules come from the in-memory ui.json bundle
 * (no FineIndex/VFS), mounting is synchronous, and there is no hot-remount (no reimports at
 * runtime) — a canvas remounts only when its `root` guid changes or the level swaps.
 */

import {
  Subsystem,
  SubsystemPriority,
  type GUID,
  type Level,
  type SubsystemConfig,
} from '@engine/core';
import * as vueRuntime from '@vue/runtime-core';
import type { App } from '@vue/runtime-core';
import {
  MsdfFontData,
  MsdfTextMeasurer,
  UIDocument,
  UIInputSystem,
  UIPass,
  UIScreenBridge,
  collectUIStyles,
  computeViewportMapping,
  createUIApp,
  createUIModuleEnv,
  createYogaLayoutEngine,
  getUIModule,
  loadUIModule,
  pollNavFromInput,
  pollPointerFromCanvas,
  registerUIFont,
  type ILayoutEngine,
  type UICanvasView,
  type UINode,
} from '@engine/ui';
import type { AssetSubsystem } from './AssetSubsystem';
import type { InputSubsystem } from './InputSubsystem';
import type { LevelSubsystem } from './LevelSubsystem';
import type { RenderSubsystem } from './RenderSubsystem';

export interface UISubsystemOptions {
  canvas: HTMLCanvasElement;
}

interface UIBundle {
  modules: Record<string, { js: string; deps: string[] }>;
  fonts: Record<string, { metrics: unknown; atlas: string }>;
  /** UI-19: shader-asset guid → compiled WGSL (UIBatch builds pipelines straight from this). */
  materials?: Record<string, string>;
  /** CSS wave 2: image-asset guid → bundled file path (decoded lazily like font atlases). */
  images?: Record<string, string>;
}

interface CanvasInstance {
  goGuid: GUID;
  rootGuid: string;
  doc: UIDocument;
  app: App<UINode>;
  view: UICanvasView;
}

interface UICanvasData {
  root?: string;
  scaleMode?: number;
  referenceHeight?: number;
  pixelPerfect?: boolean;
  antialias?: boolean;
  sortOrder?: number;
  visible?: boolean;
}

export class UISubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'ui',
    // after scripts — UI reflects this frame's game state (mirrors the editor's Runtime+6)
    priority: SubsystemPriority.Runtime + 6,
    optional: true,
    dependencies: ['assets', 'render', 'input'],
  };

  private readonly options: UISubsystemOptions;
  private engine: ILayoutEngine | null = null;
  private bundle: UIBundle | null = null;
  private readonly moduleEnv = createUIModuleEnv(vueRuntime);
  private readonly measurer = new MsdfTextMeasurer();
  private readonly canvases = new Map<GUID, CanvasInstance>();
  /** Roots that failed to mount — never retried (nothing changes at runtime), warned once. */
  private readonly failedRoots = new Set<string>();
  private views: UICanvasView[] = [];
  private readonly fontBitmaps = new Map<string, ImageBitmap | HTMLImageElement>();
  private readonly imageBitmaps = new Map<string, ImageBitmap | HTMLImageElement>();
  private readonly imagesLoading = new Set<string>();
  private passInjected = false;
  private levelId: number | null = null;
  private readonly inputSystem = new UIInputSystem(() => this.views);
  /**
   * engine.ui screen bridges, keyed by GO — they SURVIVE canvas remounts. Tagged with the
   * level id at creation: scripts' onStart can run before this subsystem's first tick on a new
   * level, so a blanket clear on level-swap would orphan just-acquired bridges (editor
   * live-verify finding) — stale-session entries are pruned by tag instead.
   */
  private readonly screens = new Map<GUID, { bridge: UIScreenBridge; levelId: number | null }>();

  constructor(options: UISubsystemOptions) {
    super();
    this.options = options;
  }

  /** Advisory pointer consumption for gameplay (`engine.ui.pointerOverUI()`, UI-9). */
  get pointerOverUI(): boolean {
    return this.inputSystem.pointerOverUI;
  }

  /** The screen bridge for a UICanvas GameObject (engine.ui.getScreen backing; lazy). */
  screenFor(goGuid: GUID): UIScreenBridge {
    const levelId = this.tryGetSubsystem<LevelSubsystem>('level')?.currentLevel?.id ?? null;
    let entry = this.screens.get(goGuid);
    if (!entry || entry.levelId !== levelId) {
      entry = { bridge: new UIScreenBridge(), levelId };
      this.screens.set(goGuid, entry);
    }
    return entry.bridge;
  }

  async onInitialize(): Promise<void> {
    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const manifest = assets.getManifest();
    if (!manifest?.ui) {
      console.log('[UISubsystem] No UI bundle in manifest — UI disabled');
      return;
    }
    this.bundle = await assets.loadJson<UIBundle>('__ui__', manifest.ui);

    // The layout wasm ships as a FILE next to rapier's (CopyRuntimeDepsStep → wasm/yoga.wasm) —
    // player bundles carry NO embedded binary (yoga-wasm esbuild plugin strips the base64).
    const wasm = await fetch('./wasm/yoga.wasm');
    if (!wasm.ok) throw new Error(`wasm/yoga.wasm missing from the build output (${wasm.status})`);
    this.engine = await createYogaLayoutEngine({ wasmBinary: await wasm.arrayBuffer() });

    // Metrics register synchronously (they ship inline); atlases decode in the background —
    // UIBatch retries a null atlas resolver every frame, so text appears when decode lands.
    for (const [guid, font] of Object.entries(this.bundle.fonts)) {
      try {
        const face = (font.metrics as { face?: { family?: string; weight?: number; italic?: boolean } }).face;
        registerUIFont(
          guid,
          MsdfFontData.parse(font.metrics as never),
          face?.family ? { face: { family: face.family, weight: face.weight, italic: face.italic } } : undefined,
        );
        void this.loadFontAtlas(assets, guid, font.atlas);
      } catch (e) {
        console.warn(`[UISubsystem] font ${guid} failed to register:`, e);
      }
    }

    console.log(
      `[UISubsystem] UI bundle loaded (${Object.keys(this.bundle.modules).length} module(s), ` +
        `${Object.keys(this.bundle.fonts).length} font(s))`,
    );
  }

  /** Sync bitmap lookup for background images; kicks the async decode on a miss (font idiom). */
  private resolveImage(guid: string): ImageBitmap | HTMLImageElement | null {
    const cached = this.imageBitmaps.get(guid);
    if (cached) return cached;
    const path = this.bundle?.images?.[guid];
    if (path && !this.imagesLoading.has(guid)) {
      this.imagesLoading.add(guid);
      void this.loadImage(guid, path);
    }
    return null;
  }

  private async loadImage(guid: string, path: string): Promise<void> {
    try {
      // loadTexture host-decodes to an HTMLImageElement in the native player (ImageSharp — the SAME
      // path sprites use, so no `Blob`/createImageBitmap), and an <img> in the browser. Both are a
      // valid TexImageSource that UIBatch uploads via createTextureFromImage.
      const assets = this.getSubsystem<AssetSubsystem>('assets');
      this.imageBitmaps.set(guid, await assets.loadTexture(`__ui_image_${guid}__`, path));
    } catch (e) {
      console.warn(`[UISubsystem] UI image ${guid} failed to load:`, e);
    }
  }

  private async loadFontAtlas(assets: AssetSubsystem, guid: string, path: string): Promise<void> {
    try {
      // Host-decoded image (see loadImage) — native-safe, no `Blob` (that was the earlier gap).
      const image = await assets.loadTexture(`__ui_font_${guid}__`, path);
      this.fontBitmaps.set(guid, image);
      for (const c of this.canvases.values()) c.doc.remeasureText();
    } catch (e) {
      console.warn(`[UISubsystem] font atlas ${guid} failed to load:`, e);
    }
  }

  onUpdate(dt: number): void {
    if (!this.engine || !this.bundle) return;

    const renderer = this.tryGetSubsystem<RenderSubsystem>('render')?.gameRenderer ?? null;
    if (renderer && !this.passInjected) {
      renderer.addPass(
        new UIPass(() => this.views, {
          resolveFontAtlas: (guid) => this.fontBitmaps.get(guid) ?? null,
          resolveMaterialWGSL: (guid) => this.bundle?.materials?.[guid] ?? null,
          resolveImage: (guid) => this.resolveImage(guid),
        }),
      );
      this.passInjected = true;
      console.log('[UISubsystem] UIPass injected into renderer');
    }

    const level = this.tryGetSubsystem<LevelSubsystem>('level')?.currentLevel ?? null;
    if ((level?.id ?? null) !== this.levelId) {
      this.disposeAll();
      this.levelId = level?.id ?? null;
      // prune bridges from OTHER levels only — this frame's scripts may already hold new ones
      for (const [guid, entry] of [...this.screens]) {
        if (entry.levelId !== this.levelId) this.screens.delete(guid);
      }
    }
    if (!level) return;

    const canvas = this.options.canvas;
    const physW = canvas.width;
    const physH = canvas.height;

    const present = new Set<GUID>();
    for (const go of level.getAllObjects()) {
      const data = go.getComponentData('core.UICanvas') as UICanvasData | null;
      if (!data) continue;
      present.add(go.guid);

      const rootGuid = data.root ?? '';
      let instance = this.canvases.get(go.guid);
      if (instance && instance.rootGuid !== rootGuid) {
        this.disposeCanvas(go.guid);
        instance = undefined;
      }
      if (!instance) {
        if (rootGuid && !this.failedRoots.has(rootGuid)) this.mountCanvas(go.guid, rootGuid);
        instance = this.canvases.get(go.guid);
        if (!instance) continue;
      }

      instance.view.sortOrder = data.sortOrder ?? 0;
      instance.view.visible = data.visible !== false;
      instance.view.pixelPerfect = data.pixelPerfect !== false;
      instance.view.antialias = data.antialias !== false;
      if (physW > 0 && physH > 0) {
        const mapping = computeViewportMapping(physW, physH, {
          mode: data.scaleMode === 1 ? 'constant-pixel' : 'scale-with-height',
          referenceHeight: data.referenceHeight ?? 1080,
        });
        instance.view.scale = mapping.scale;
        instance.doc.setViewport(mapping.logicalWidth, mapping.logicalHeight);
        instance.doc.update(dt);
      }
    }

    for (const guid of [...this.canvases.keys()]) {
      if (!present.has(guid)) this.disposeCanvas(guid);
    }

    const input = this.tryGetSubsystem<InputSubsystem>('input');
    if (input && this.canvases.size > 0) {
      this.inputSystem.update(
        dt,
        pollPointerFromCanvas(input.manager, canvas),
        pollNavFromInput(input.manager),
      );
    }
  }

  private mountCanvas(goGuid: GUID, rootGuid: string): void {
    try {
      this.loadChain(rootGuid, new Set());
      const mod = getUIModule(rootGuid);
      if (!mod || !this.engine) throw new Error(`UI module ${rootGuid} did not load`);

      const doc = new UIDocument(this.engine, { measurer: this.measurer });
      for (const sheet of collectUIStyles(rootGuid)) doc.addStyleSheet(sheet);
      const app = createUIApp(doc, mod.component, this.screenFor(goGuid).props);
      this.canvases.set(goGuid, {
        goGuid,
        rootGuid,
        doc,
        app,
        view: { document: doc, scale: 1, sortOrder: 0, visible: true, pixelPerfect: true },
      });
      this.rebuildViews();
      console.log(`[UISubsystem] Mounted UI canvas ${rootGuid} on ${goGuid}`);
    } catch (e) {
      this.failedRoots.add(rootGuid);
      console.error(`[UISubsystem] Failed to mount UI canvas '${rootGuid}':`, e);
    }
  }

  /** Evaluate a module + its .uic deps from the bundle, deps-first (`__uic` runs at eval time). */
  private loadChain(guid: string, stack: Set<string>): void {
    if (stack.has(guid)) throw new Error(`circular .uic imports at ${guid}`);
    stack.add(guid);

    const cached = getUIModule(guid);
    if (cached) {
      for (const dep of cached.deps) this.loadChain(dep, stack);
      return;
    }
    const entry = this.bundle?.modules[guid];
    if (!entry) throw new Error(`UI module ${guid} is not in the build's ui.json`);
    for (const dep of entry.deps) this.loadChain(dep, stack);
    loadUIModule(guid, entry.js, this.moduleEnv);
  }

  private tryGetSubsystem<T extends Subsystem>(name: string): T | null {
    try {
      return this.getSubsystem<T>(name) ?? null;
    } catch {
      return null;
    }
  }

  private rebuildViews(): void {
    this.views = [...this.canvases.values()].map((c) => c.view);
  }

  private disposeCanvas(goGuid: GUID): void {
    const instance = this.canvases.get(goGuid);
    if (!instance) return;
    this.canvases.delete(goGuid);
    try {
      instance.app.unmount();
      instance.doc.dispose();
    } catch (e) {
      console.warn('[UISubsystem] UI canvas dispose failed:', e);
    }
    this.rebuildViews();
  }

  private disposeAll(): void {
    this.inputSystem.reset();
    for (const guid of [...this.canvases.keys()]) this.disposeCanvas(guid);
  }

  onShutdown(): void {
    this.disposeAll();
    this.screens.clear();
    this.fontBitmaps.clear();
    this.imageBitmaps.clear();
    this.imagesLoading.clear();
    this.bundle = null;
  }
}
